import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import ChartContent, {ChartContentChart} from 'charts/chart-content/ChartContent';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertPipelineStepRawDataIsKind} from 'pipelines/PipelineTypes';

import getDownloadAsPNGAction from './chart-view/getDownloadAsPNGAction';
import DataViewContext, {OverflowDataViewAction, useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';

/**
 * Render view for charts.
 *
 * @package
 */
export default function ChartView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {
    analysisView: {lineage},
    previewData,
  } = dataViewContextValue;

  const {renderedChart, actions} = useMemo(() => {
    const isPreview = previewData != null;
    assert(
      !isPreview || previewData.previewDraftStepData.chartPreviews.length === 1,
      'Expected one chart preview'
    );

    const stepOutputReference = lineage[lineage.length - 1].stepOutputReference!;
    const rawData = stepOutputReference.outputStepData.rawDatas[stepOutputReference.rawDataIndex!];

    let renderedChart: ChartContentChart;
    const actions: Array<OverflowDataViewAction> = [];
    if (!isPreview) {
      assertPipelineStepRawDataIsKind(rawData, PipelineStepDataKind.CHART);
      renderedChart = rawData;
      actions.push(getDownloadAsPNGAction(rawData));
    } else {
      renderedChart = previewData.previewDraftStepData.chartPreviews[0];
    }

    return {isPreview, chartNode: rawData, renderedChart, actions};
  }, [lineage, previewData]);

  const newDataFrameViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      isChartView: true,
      actions: {
        ...dataViewContextValue.actions,
        overflowActions: [...dataViewContextValue.actions.overflowActions, ...actions],
      },
      dataTestAttrs: {component: 'ChartView', element: 'root'},
    }),
    [dataViewContextValue, actions]
  );

  return (
    <DataViewContext.Provider value={newDataFrameViewContextValue}>
      <DataViewPane>
        <ChartContent chart={renderedChart} />
      </DataViewPane>
    </DataViewContext.Provider>
  );
}
